import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSpvSca } from 'src/hooks/useSpvSca';
import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from '@mui/lab/LoadingButton';
import { IconButton, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';

interface Props {
  open: boolean;
  handleClose: () => void;
  spvId: string;
  handleCloseAfterSuccess?: () => void;
}

export const AddScaModal: FC<Props> = ({ open, handleClose, spvId, handleCloseAfterSuccess }) => {
  const { t } = useTranslation();
  const { refresh, startConsent, enroll } = useSpvSca();
  const [modalSteps, setModalSteps] = useState<number>(0);
  const [consentLoading, setConsentLoading] = useState<boolean>(false);
  const [enrollLoading, setEnrollLoading] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [note, setNote] = useState<string>('');

  const resetForm = () => {
    setModalSteps(0);
    setConsentLoading(false);
    setEnrollLoading(false);
    setRefreshing(false);
    setError('');
    setNote('');
  };

  const closeModal = () => {
    resetForm();
    handleClose();
  };

  const handleConsent = async () => {
    setError('');
    setNote('');
    setConsentLoading(true);
    const url = await startConsent(spvId);
    setConsentLoading(false);
    if (url) {
      window.open(url, '_blank');
      setModalSteps(1);
    } else {
      setError(t('payment.account.sca.modal.error'));
    }
  };

  const handleEnroll = async () => {
    setError('');
    setNote('');
    setEnrollLoading(true);
    const url = await enroll(spvId);
    setEnrollLoading(false);
    if (url) {
      window.open(url, '_blank');
      setModalSteps(1);
    } else {
      // null redirectUrl means the SPV owner is already enrolled
      setNote(t('payment.account.sca.modal.alreadyEnrolled'));
      setModalSteps(1);
    }
  };

  const handleRefreshStatus = async () => {
    setRefreshing(true);
    await refresh(spvId);
    setRefreshing(false);
    resetForm();
    if (handleCloseAfterSuccess) {
      handleCloseAfterSuccess();
    } else {
      handleClose();
    }
  };

  return (
    <div>
      {/* First screen: choose an action */}
      {modalSteps === 0 && (
        <Dialog open={open} onClose={closeModal} fullWidth>
          <DialogTitle>
            <Grid container>
              <Typography flexGrow={1} variant="h6">
                {t('payment.account.sca.modal.title')}
              </Typography>
              <IconButton onClick={closeModal}>
                <CloseIcon />
              </IconButton>
            </Grid>
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>{t('payment.account.sca.modal.description')}</DialogContentText>
            {error && <Typography color="red">{error}</Typography>}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeModal} color="primary">
              {t('payment.account.sca.modal.cancel')}
            </Button>
            <LoadingButton variant="outlined" loading={enrollLoading} disabled={consentLoading} onClick={handleEnroll}>
              {t('payment.account.sca.modal.enroll')}
            </LoadingButton>
            <LoadingButton variant="contained" loading={consentLoading} disabled={enrollLoading} onClick={handleConsent}>
              {t('payment.account.sca.modal.grantConsent')}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}

      {/* Second screen: complete in opened tab, then refresh */}
      {modalSteps === 1 && (
        <Dialog open={open} onClose={closeModal} fullWidth>
          <DialogTitle>
            <Grid container>
              <Typography flexGrow={1} variant="h6">
                {t('payment.account.sca.modal.pending.title')}
              </Typography>
              <IconButton onClick={closeModal}>
                <CloseIcon />
              </IconButton>
            </Grid>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>{note || t('payment.account.sca.modal.pending.description')}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={closeModal}>
              {t('payment.account.sca.modal.close')}
            </Button>
            <LoadingButton variant="contained" loading={refreshing} onClick={handleRefreshStatus}>
              {t('payment.account.sca.modal.refreshStatus')}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};
